import * as React from 'react';
import { CalendarDays, Heart, MapPin, Star, UserRound } from 'lucide-react';
import { Link } from 'wouter';
import { DELIVERY_MODE_LABELS, isDeliveryMode } from '@shared/delivery-mode';
import { isDemoClassTitle } from '@shared/demo-classes';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { normalizeImageUrl, imageErrorFallback, PLACEHOLDER } from '@/lib/image';
import { trackPublicFunnelEvent } from '@/lib/public-analytics';
import { cn } from '@/lib/utils';

type ClassCardVariant = 'compact' | 'comfortable' | 'expanded';

interface ClassCardProps {
  id: string;
  title: string;
  description: string;
  price: string | number;
  pricingMode?: string | null;
  suggestedPrice?: string | number | null;
  promoEnabled?: boolean | null;
  promoPrice?: string | number | null;
  promoEndsAt?: string | null;
  thumbnailUrl?: string | null;
  category?: string | null;
  rating?: string | number | null;
  deliveryMode?: string | null;
  creator?: {
    id: string;
    displayName: string;
    profileImageUrl?: string | null;
  } | null;
  enrollmentCount?: number;
  spotsLeft?: number | null;
  localityName?: string | null;
  venue?: {
    name?: string | null;
    slug?: string | null;
    address?: string | null;
  } | null;
  venueHref?: string | null;
  sessionLabel?: string | null;
  detailHref?: string;
  variant?: ClassCardVariant;
  className?: string;
}

const variantMap: Record<
  ClassCardVariant,
  {
    imageHeight: string;
    wrapper: string;
    title: string;
    description: string;
    actionLabel: string;
  }
> = {
  compact: {
    imageHeight: 'h-40',
    wrapper: 'rounded-[1.25rem] border border-border/70 p-3 shadow-sm',
    title: 'text-lg',
    description: 'line-clamp-2 text-sm',
    actionLabel: 'See class',
  },
  comfortable: {
    imageHeight: 'h-48',
    wrapper: 'rounded-[1.5rem] border border-border/70 p-4 shadow-sm',
    title: 'text-xl',
    description: 'line-clamp-2 text-sm',
    actionLabel: 'View Details',
  },
  expanded: {
    imageHeight: 'h-56',
    wrapper: 'rounded-[1.75rem] border border-border/70 p-5 shadow-md',
    title: 'text-2xl',
    description: 'line-clamp-3 text-base',
    actionLabel: 'Book now',
  },
};

function formatStandardPrice(price: string | number) {
  if (typeof price === 'string' && price.trim().startsWith('$')) {
    return price.trim();
  }
  return `$${price}`;
}

/**
 * Resolve how the price should render. When a promo is active, `original` holds
 * the MSRP (to be struck through) and `current` holds the discounted price (or "Free").
 */
function resolvePriceDisplay(
  price: string | number,
  pricingMode?: string | null,
  suggestedPrice?: string | number | null,
  promoEnabled?: boolean | null,
  promoPrice?: string | number | null,
  promoEndsAt?: string | null
): { current: string; original: string | null } {
  if (pricingMode === 'free') return { current: 'Free', original: null };
  if (pricingMode === 'pay_what_you_want') {
    if (suggestedPrice != null && Number(suggestedPrice) > 0) {
      return { current: `From $${Number(suggestedPrice).toFixed(0)}`, original: null };
    }
    return { current: 'Pay what you want', original: null };
  }
  // A windowed promo only applies to sessions inside the window. The card can't
  // be session-specific, so once the window has fully ended, show the standard
  // price rather than a stale "Free" / discount.
  const promoLapsed = promoEndsAt != null && new Date(promoEndsAt).getTime() < Date.now();
  // Active promo on a standard-priced class
  if (promoEnabled && promoPrice != null && !promoLapsed) {
    const promoNum = Number(promoPrice);
    return {
      current: promoNum <= 0 ? 'Free' : formatStandardPrice(promoPrice),
      original: formatStandardPrice(price),
    };
  }
  return { current: formatStandardPrice(price), original: null };
}

export default function ClassCard({
  id,
  title,
  description,
  price,
  pricingMode,
  suggestedPrice,
  promoEnabled,
  promoPrice,
  promoEndsAt,
  thumbnailUrl,
  category,
  rating,
  deliveryMode,
  creator,
  spotsLeft,
  localityName,
  venue,
  venueHref,
  sessionLabel,
  detailHref,
  variant = 'comfortable',
  className,
}: ClassCardProps) {
  const styles = variantMap[variant];
  const href = detailHref ?? `/classes/${id}`;
  const isDemoClass = isDemoClassTitle(title);
  const priceDisplay = resolvePriceDisplay(
    price,
    pricingMode,
    suggestedPrice,
    promoEnabled,
    promoPrice,
    promoEndsAt
  );
  const [creatorImageFailed, setCreatorImageFailed] = React.useState(false);
  const creatorImageUrl =
    creator?.profileImageUrl && !creatorImageFailed
      ? normalizeImageUrl(creator.profileImageUrl, '')
      : null;

  React.useEffect(() => {
    setCreatorImageFailed(false);
  }, [creator?.profileImageUrl]);
  const displayRating = rating ? Number(rating).toFixed(1) : 'New';
  const deliveryLabel =
    deliveryMode && isDeliveryMode(deliveryMode)
      ? DELIVERY_MODE_LABELS[deliveryMode]
      : (deliveryMode ?? null);

  const handleClassDetailClick = () => {
    void trackPublicFunnelEvent({
      eventName: 'discovery_to_detail',
      source: 'class_card',
      destinationType: 'class_detail',
      classId: id,
    });
  };

  const handleCreatorDetailClick = () => {
    if (!creator?.id) {
      return;
    }

    void trackPublicFunnelEvent({
      eventName: 'discovery_to_detail',
      source: 'class_card',
      destinationType: 'creator_detail',
      classId: id,
      creatorId: creator.id,
    });
  };

  return (
    <article
      className={cn(
        'card-hover group flex h-full flex-col overflow-hidden bg-card transition-transform duration-200 hover:-translate-y-1',
        styles.wrapper,
        className
      )}
      data-testid={`class-card-${id}`}
    >
      <Link
        href={href}
        onClick={handleClassDetailClick}
        className="block overflow-hidden rounded-[calc(var(--radius)+0.25rem)]"
      >
        <img
          src={normalizeImageUrl(thumbnailUrl, PLACEHOLDER.thumb)}
          alt={title}
          className={cn(
            'w-full object-cover transition duration-300 group-hover:scale-[1.03]',
            styles.imageHeight
          )}
          data-testid={`class-thumbnail-${id}`}
          onError={imageErrorFallback(PLACEHOLDER.thumb, 'Class Thumbnail')}
        />
      </Link>

      <div className="flex flex-1 flex-col pt-4">
        <div className="flex flex-wrap items-center gap-2">
          {category ? (
            <Badge
              variant="secondary"
              className="rounded-full px-3 py-1 text-xs"
              data-testid={`class-category-${id}`}
            >
              {category}
            </Badge>
          ) : null}
          {deliveryLabel ? (
            <Badge variant="outline" className="rounded-full px-3 py-1 text-xs">
              {deliveryLabel}
            </Badge>
          ) : null}
          {spotsLeft != null && spotsLeft <= 0 ? (
            <Badge variant="destructive" className="rounded-full px-3 py-1 text-xs">
              Next session sold out
            </Badge>
          ) : spotsLeft != null && spotsLeft <= 5 ? (
            <Badge
              variant="outline"
              className="rounded-full border-orange-400 px-3 py-1 text-xs text-orange-600"
            >
              {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} left
            </Badge>
          ) : null}
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Star className="h-3.5 w-3.5 fill-current text-accent" />
            {displayRating}
          </span>
        </div>

        <Link href={href} onClick={handleClassDetailClick}>
          <h3
            className={cn(
              'mt-3 font-semibold leading-tight text-foreground transition-colors hover:text-primary group-hover:text-primary',
              styles.title
            )}
            data-testid={`class-title-${id}`}
          >
            {title}
          </h3>
        </Link>

        <p
          className={cn('mt-3 flex-1 leading-6 text-muted-foreground', styles.description)}
          data-testid={`class-description-${id}`}
        >
          {description}
        </p>

        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          {venue?.name ? (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {venueHref ? (
                <Link
                  href={venueHref}
                  className="font-medium text-foreground/80 hover:text-primary"
                >
                  {venue.name}
                </Link>
              ) : (
                <span className="font-medium text-foreground/80">{venue.name}</span>
              )}
            </div>
          ) : null}
          {localityName ? (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{localityName}</span>
            </div>
          ) : null}
          {sessionLabel ? (
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span>{sessionLabel}</span>
            </div>
          ) : null}
          {spotsLeft != null && spotsLeft <= 0 ? (
            <div className="flex items-center gap-2 font-medium text-orange-600">
              <CalendarDays className="h-4 w-4 shrink-0" />
              <span>The next session is full — check the class for other dates.</span>
            </div>
          ) : null}
        </div>

        <div className="mt-5 flex items-end justify-between gap-4">
          <div className="min-w-0 flex-1">
            {creator ? (
              <div className="flex items-center gap-3">
                <Link
                  href={`/creators/${creator.id}`}
                  onClick={handleCreatorDetailClick}
                  className="shrink-0"
                >
                  {creatorImageUrl ? (
                    <img
                      src={creatorImageUrl}
                      alt={creator.displayName}
                      className="h-10 w-10 rounded-full object-cover ring-1 ring-border/60"
                      data-testid={`creator-avatar-${creator.id}`}
                      onError={() => setCreatorImageFailed(true)}
                    />
                  ) : (
                    <div
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground ring-1 ring-border/60"
                      data-testid={`creator-avatar-placeholder-${creator.id}`}
                      aria-label={`${creator.displayName} has not uploaded a profile photo`}
                    >
                      <UserRound className="h-5 w-5" />
                    </div>
                  )}
                </Link>
                <div className="min-w-0">
                  <Link
                    href={`/creators/${creator.id}`}
                    onClick={handleCreatorDetailClick}
                    className="block truncate text-sm font-medium text-foreground/80 hover:text-primary"
                    data-testid={`creator-name-${creator.id}`}
                  >
                    {creator.displayName}
                  </Link>
                  <p
                    className="mt-1 text-xl font-semibold text-foreground"
                    data-testid={`class-price-${id}`}
                  >
                    {priceDisplay.original ? (
                      <>
                        <span className="mr-2 text-base font-normal text-muted-foreground line-through">
                          {priceDisplay.original}
                        </span>
                        <span className="text-primary">{priceDisplay.current}</span>
                      </>
                    ) : (
                      priceDisplay.current
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <p
                  className="mt-1 text-xl font-semibold text-foreground"
                  data-testid={`class-price-${id}`}
                >
                  {priceDisplay.original ? (
                    <>
                      <span className="mr-2 text-base font-normal text-muted-foreground line-through">
                        {priceDisplay.original}
                      </span>
                      <span className="text-primary">{priceDisplay.current}</span>
                    </>
                  ) : (
                    priceDisplay.current
                  )}
                </p>
              </>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              className="touch-target rounded-full px-3"
              data-testid={`button-favorite-${id}`}
              aria-label={`Save ${title}`}
            >
              <Heart className="h-4 w-4" />
            </Button>
            {isDemoClass ? (
              <Button
                disabled
                className="touch-target rounded-full px-4"
                data-testid={`button-enroll-${id}`}
              >
                Demo Class
              </Button>
            ) : (
              <Link href={href} onClick={handleClassDetailClick}>
                <Button
                  className="touch-target rounded-full px-4"
                  data-testid={`button-enroll-${id}`}
                >
                  {styles.actionLabel}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
